import { postJSON } from "../api.js";
import { esc } from "../ui/format.js";
import { uiGet, uiHeader, uiKpis, uiMoney } from "../ui/kit.js";
import { toast } from "../ui/modals.js";
import { renderAmbassadors } from "./ambassadors.js";

/**
 * Marketing — the Ad Studio (backend/marketing.py; tab key "ambassadors" for old links).
 *
 * Rachit (2026-10-15): "I don't like advertisement, create it more best."
 * An ad is built the way a real campaign is: WHAT you sell (a product or the
 * whole brand), WHERE (your industry's six real channels), WHAT IT SAYS (the
 * message) and WHO it's for. A live mock-up of the ad updates as you choose,
 * with one result line. Its creative strength (how well product, message,
 * channel and audience fit) scales the effect; a focus group reveals it
 * exactly before you spend. Live campaigns report every month: people
 * reached, responses and sales won. Brand ambassadors fold away underneath.
 */

const mkM = (v) => uiMoney(v, { compact: true });
const mkN = (n) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${Math.round(n / 1e3)}K` : `${Math.round(n)}`);
// how each kind of channel looks in the preview
const MK_FRAME = { Performance: "phone", Brand: "screen", Trade: "booth", Sales: "letter", Content: "page", Retention: "phone",
  Relations: "letter", Community: "poster" };
const MK_FRAME_LABEL = { phone: "On their phone", screen: "On the big screen", booth: "At the event", letter: "In their inbox",
  page: "In their search", poster: "Around town" };

// the ad being built survives a re-render (every action refreshes the tab)
const mkAd = { product: null, channel: null, angle: null, audience: null, monthly: null, months: 3 };

function mkStrength(c) {
  if (!c) return `<div class="ad-str muted">Pick a message and an audience to see how strong the ad is.</div>`;
  const v = c.tested ? c.score : c.looks;
  const tone = v >= 70 ? "good" : v >= 50 ? "ok" : "bad";
  return `<div class="ad-str ${tone}">
    <div class="ad-str-top"><span>Creative strength</span><b>${c.tested ? `${v}/100` : `about ${v}/100`}</b></div>
    <div class="ad-str-bar"><i style="width:${v}%"></i></div>
    <em>${c.tested ? `Tested with a focus group: the campaign works at ×${c.multiplier.toFixed(2)}.` : "On paper. Real people may like it more or less: test it to be sure."}</em>
  </div>`;
}

function mkPreview(d, ch, q) {
  const frame = MK_FRAME[ch.kind] || "poster";
  const prod = d.products.find((p) => p.id === mkAd.product);
  const angle = d.angles.find((a) => a.key === mkAd.angle);
  const head = q && q.creative ? q.creative.headline : angle ? `${prod ? prod.name : d.company}: ${angle.what}` : "Your headline appears here";
  const cta = d.audience_set === "business" ? "Book a demo" : "Shop now";
  return `<div class="ad-frame ad-${frame}" aria-label="Preview of your ad">
      <span class="ad-where">${esc(MK_FRAME_LABEL[frame])} · ${ch.icon} ${esc(ch.name)}</span>
      <div class="ad-art">
        <span class="ad-brand">${esc(d.company)}</span>
        <h4 class="ad-head">${esc(head)}</h4>
        ${angle ? `<span class="ad-tag">${angle.icon} ${esc(angle.name)}</span>` : ""}
        <span class="ad-cta">${cta} →</span>
      </div>
    </div>`;
}

function mkResult(q) {
  if (!q) return `<div class="ad-result">…</div>`;
  return `<div class="ad-result">
      <div class="mk-big">+${q.growth_pts}% sales a month <span>for ${q.growth_months} months</span></div>
      <div class="mk-small">Reaches about ${mkN(q.reach_month)} people a month · +${q.brand} brand · about ${mkM(q.extra_revenue)} extra revenue over ${q.horizon_months} months · costs ${mkM(q.total)}</div>
      ${q.saturation_pct ? `<div class="mk-small warn">Your other campaigns reach the same people: ${q.saturation_pct}% less effect.</div>` : ""}
    </div>`;
}

function mkLive(c) {
  const max = Math.max(1, ...c.log.map((x) => x.sales));
  return `<article class="ad-live">
    <div class="ad-live-top"><span class="mk-icon">${c.icon}</span><div><b>${esc(c.name)}</b>
      <span>${c.product_name ? esc(c.product_name) : "Whole brand"} · ${c.months_left} month${c.months_left === 1 ? "" : "s"} left · ${mkM(c.monthly)}/mo</span></div>
      ${c.score_pct != null ? `<i class="ad-chip ${c.score_pct >= 70 ? "good" : c.score_pct >= 50 ? "" : "bad"}">${c.score_pct}/100</i>` : ""}</div>
    ${c.headline ? `<blockquote>“${esc(c.headline)}”</blockquote>` : ""}
    ${c.log.length ? `<div class="ad-bars" role="img" aria-label="Sales won each month">${c.log.map((x) => `<div title="Month ${x.month}: reached ${mkN(x.reach)} people, ${mkN(x.responses)} responded, about ${mkM(x.sales)} in sales">
        <i style="height:${Math.max(6, Math.round((100 * x.sales) / max))}%"></i><em>M${x.month}</em></div>`).join("")}</div>
      <p class="ad-live-sum">Reached <b>${mkN(c.reach_total)}</b> people · sales won about <b>${mkM(c.sales_won)}</b>${c.roi != null ? ` · every $1 brought back <b>$${c.roi.toFixed(2)}</b>` : ""}</p>`
      : `<p class="ad-live-sum">First results arrive when this month closes.</p>`}
    <div class="ad-live-act"><button type="button" class="ofr-quiet" data-mk-stop="${c.id}">Stop it</button></div>
  </article>`;
}

function mkPast(list) {
  if (!list.length) return "";
  const stars = (r) => (r == null ? "—" : r >= 2 ? "★★★" : r >= 1 ? "★★☆" : r >= 0.5 ? "★☆☆" : "☆☆☆");
  return `<details class="sx-card ad-past"><summary>Past campaigns (${list.length})</summary>
    <table class="sx-table"><tr><th>Campaign</th><th>Ad</th><th class="num">Spent</th><th class="num">Sales won</th><th class="num">Return</th></tr>
    ${list.map((c) => `<tr><td>${c.icon} ${esc(c.name)}</td><td>${c.headline ? `“${esc(c.headline)}”` : "—"}</td><td class="num">${mkM(c.spent)}</td>
      <td class="num">${mkM(c.sales_won)}</td><td class="num" title="${c.roi != null ? `$${c.roi.toFixed(2)} back for every $1` : ""}">${stars(c.roi)}</td></tr>`).join("")}</table></details>`;
}

async function renderMarketing(main, snap) {
  const d = await uiGet("/marketing");
  d.company = (window.__company && window.__company.name) || "Your company";
  const full = d.running.length >= d.max_active;
  const live = new Set(d.running.map((c) => c.channel));
  const free = d.playbook.filter((c) => !live.has(c.key));
  if (!d.products.some((p) => p.id === mkAd.product)) mkAd.product = d.products.length ? d.products[0].id : null;
  if (!free.some((c) => c.key === mkAd.channel)) { mkAd.channel = free.length ? free[0].key : null; mkAd.monthly = null; }
  if (!d.audiences.some((a) => a.key === mkAd.audience)) mkAd.audience = null;
  const won = d.running.concat(d.past).reduce((s, c) => s + (c.sales_won || 0), 0);

  const tiles = (list, key, render) => list.map((x) => `<button type="button" class="ad-tile${mkAd[key] === x.key ? " on" : ""}" data-pick="${key}" data-val="${esc(String(x.key))}" ${x.disabled ? "disabled" : ""}>${render(x)}</button>`).join("");
  main.innerHTML = `
    <div class="sx mk">
      ${uiHeader(`Marketing — Month ${d.month}`, "The Ad Studio. Choose what you sell, where, what the ad says and who it's for. Test it if you like, then launch it and watch what it sells.")}
      ${uiKpis([
        { label: "Spending now", value: `${mkM(d.spend_now)}/mo`, sub: d.norm && d.norm.pct !== null ? `all sales & marketing ${d.norm.pct.toFixed(1)}% of revenue · normal ${d.norm.benchmark_pct.toFixed(0)}%` : "" },
        { label: "Extra growth", value: `+${d.growth_now}%`, sub: "a month, from campaigns" },
        { label: "Sales won by ads", value: mkM(won), sub: "estimated, all campaigns" },
        { label: "Brand", value: `${Math.round(d.brand)}`, sub: "out of 100" },
      ])}
      ${full || !free.length ? `<p class="mk-note ad-full">${d.max_active} campaigns are running: launch the next one when one ends (or stop one).</p>` : `
      <section class="ad-studio">
        <div class="ad-steps">
          <div class="ad-step"><h4><i>1</i> What are you advertising?</h4>
            <div class="ad-chips">${d.products.map((p) => `<button type="button" class="ad-chipbtn${mkAd.product === p.id ? " on" : ""}" data-pick="product" data-val="${p.id}">${esc(p.name)} <em>${esc(p.tier)}</em></button>`).join("")}
              <button type="button" class="ad-chipbtn${mkAd.product === null ? " on" : ""}" data-pick="product" data-val="">The whole brand</button></div></div>
          <div class="ad-step"><h4><i>2</i> Where will people see it?</h4>
            <div class="ad-tiles six">${tiles(d.playbook.map((c) => ({ ...c, disabled: live.has(c.key) })), "channel", (c) => `<span class="ad-ico">${c.icon}</span><b>${esc(c.name)}</b><span>${live.has(c.key) ? "● Already live" : `${esc(c.best_for)} · from ${mkM(c.min)}/mo`}</span>`)}</div></div>
          <div class="ad-step"><h4><i>3</i> What does the ad say?</h4>
            <div class="ad-tiles">${tiles(d.angles, "angle", (a) => `<span class="ad-ico">${a.icon}</span><b>${esc(a.name)}</b><span>${esc(a.what)}</span>${a.risky ? `<em class="ad-risk">risky</em>` : ""}`)}</div></div>
          <div class="ad-step"><h4><i>4</i> Who is it for?</h4>
            <div class="ad-tiles four">${tiles(d.audiences, "audience", (a) => `<span class="ad-ico">${a.icon}</span><b>${esc(a.name)}</b><span>${esc(a.who)}</span>`)}</div></div>
          <div class="ad-step"><h4><i>5</i> How big?</h4>
            <label class="mk-label">Budget a month <b id="ad-amt"></b></label>
            <input type="range" id="ad-range" step="1000" aria-label="Budget a month">
            <div class="mk-months">${[1, 3, 6].map((m) => `<button type="button" data-m="${m}" class="${m === mkAd.months ? "on" : ""}">${m} month${m > 1 ? "s" : ""}</button>`).join("")}</div></div>
        </div>
        <aside class="ad-side">
          <div id="ad-preview"></div>
          <div id="ad-strength"></div>
          <div id="ad-result"></div>
          <div class="ad-go">
            <button type="button" id="ad-test" disabled>🧪 Test with a focus group</button>
            <button type="button" class="primary" id="ad-launch" disabled>🚀 Launch the ad</button>
          </div>
        </aside>
      </section>`}
      ${d.running.length ? `<h3 class="mk-h ad-h">Live campaigns</h3><div class="ad-lives">${d.running.map(mkLive).join("")}</div>` : ""}
      ${mkPast(d.past)}
      <details class="sx-card mk-amb-fold"><summary>⭐ Brand ambassadors: famous faces for your brand</summary><div id="mk-amb"></div></details>
    </div>`;

  main.querySelectorAll("[data-mk-stop]").forEach((b) => b.addEventListener("click", async () => {
    if (!confirm("Stop this campaign? No more monthly bills; what it already built fades out on its own.")) return;
    try { toast((await postJSON("/actions/execute", { action: { type: "marketing_stop", params: { id: +b.dataset.mkStop } } })).message); await window.refreshAll(); }
    catch (e) { toast(e.message, true); }
  }));
  const amb = main.querySelector(".mk-amb-fold");
  // a scandal letter must not hide inside a closed fold
  const ambDone = renderAmbassadors(main.querySelector("#mk-amb"), snap, { embedded: true })
    .then(() => { if (amb.querySelector(".em-letter")) amb.open = true; }).catch(() => {});
  if (full || !free.length) return ambDone;

  const range = main.querySelector("#ad-range"), testBtn = main.querySelector("#ad-test"), goBtn = main.querySelector("#ad-launch");
  let quote = null, timer = 0, seq = 0;
  const channel = () => d.playbook.find((c) => c.key === mkAd.channel);
  const setRange = () => {
    const ch = channel();
    const max = Math.max(ch.min * 10, Math.round((d.revenue * 0.3) / 1000) * 1000, ch.min * 2);
    const start = mkAd.monthly && mkAd.monthly >= ch.min && mkAd.monthly <= max ? mkAd.monthly : Math.min(max, Math.max(ch.min, Math.round((d.revenue * 0.05) / 1000) * 1000));
    range.min = ch.min; range.max = max; range.value = start;
    mkAd.monthly = +range.value;
  };
  const paintStatic = () => {
    main.querySelector("#ad-amt").textContent = mkM(+range.value);
    main.querySelector("#ad-preview").innerHTML = mkPreview(d, channel(), quote);
    main.querySelector("#ad-strength").innerHTML = mkStrength(quote && quote.creative);
    main.querySelector("#ad-result").innerHTML = mkResult(quote);
    const c = quote && quote.creative;
    testBtn.disabled = !c || c.tested;
    testBtn.textContent = !c ? "🧪 Test with a focus group" : c.tested ? "🧪 Tested" : `🧪 Test with a focus group (${mkM(c.test_cost)})`;
    goBtn.disabled = !quote || !mkAd.angle || !mkAd.audience;
    goBtn.textContent = !mkAd.angle || !mkAd.audience ? "🚀 Pick a message and an audience" : quote ? `🚀 Launch for ${mkM(quote.monthly)} a month` : "🚀 Launch the ad";
  };
  let firstQuote = true; // the first quote goes at once; the wait is only for dragging (2.7: the tab waited 180 ms for nothing)
  const fetchQuote = () => {
    paintStatic();
    clearTimeout(timer);
    const wait = firstQuote ? 0 : 180;
    firstQuote = false;
    timer = setTimeout(async () => {
      const mine = ++seq;
      const qs = new URLSearchParams({ channel: mkAd.channel, monthly: range.value, months: mkAd.months, product_id: mkAd.product || 0,
        angle: mkAd.angle || "", audience: mkAd.audience || "" });
      try { const q = await uiGet(`/marketing/quote?${qs}`); if (mine === seq) { quote = q; paintStatic(); } }
      catch (e) { if (mine === seq) main.querySelector("#ad-result").innerHTML = `<div class="ad-result"><span class="neg">${esc(e.message)}</span></div>`; }
    }, wait);
  };
  setRange();
  range.addEventListener("input", () => { mkAd.monthly = +range.value; fetchQuote(); });
  main.querySelectorAll("[data-m]").forEach((b) => b.addEventListener("click", () => {
    mkAd.months = +b.dataset.m;
    main.querySelectorAll("[data-m]").forEach((x) => x.classList.toggle("on", x === b));
    fetchQuote();
  }));
  main.querySelectorAll("[data-pick]").forEach((b) => b.addEventListener("click", () => {
    const k = b.dataset.pick;
    mkAd[k] = k === "product" ? (b.dataset.val ? +b.dataset.val : null) : b.dataset.val;
    main.querySelectorAll(`[data-pick="${k}"]`).forEach((x) => x.classList.toggle("on", x === b));
    if (k === "channel") { mkAd.monthly = null; setRange(); }
    quote = null;
    fetchQuote();
  }));
  const params = () => ({ channel: mkAd.channel, monthly: +range.value, months: mkAd.months, product_id: mkAd.product, angle: mkAd.angle, audience: mkAd.audience });
  testBtn.addEventListener("click", async () => {
    testBtn.disabled = true;
    try { toast((await postJSON("/actions/execute", { action: { type: "marketing_test", params: params() } })).message); await window.refreshAll(); }
    catch (e) { testBtn.disabled = false; toast(e.message, true); }
  });
  goBtn.addEventListener("click", async () => {
    goBtn.disabled = true;
    try {
      toast((await postJSON("/actions/execute", { action: { type: "marketing_launch", params: params() } })).message);
      mkAd.angle = null; mkAd.audience = null; mkAd.channel = null;
      await window.refreshAll();
    } catch (e) { goBtn.disabled = false; toast(e.message, true); }
  });
  fetchQuote();
  return ambDone;
}

window.renderMarketing = renderMarketing;

export { renderMarketing };
